import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import './FAQPage.css';

const FAQS = [
  {
    q: 'What regions do you serve?',
    a: 'Our primary coverage area spans Florida, Georgia, Alabama, South Carolina, Tennessee, and the Gulf Coast. We also handle East Coast corridors and select national lanes. Contact us with your origin/destination pair and we\'ll confirm coverage immediately.',
  },
  {
    q: 'Do you handle LTL (less-than-truckload) shipments?',
    a: 'Yes. We handle both LTL and FTL (full truckload) freight. For LTL, we leverage our carrier network to consolidate loads and keep your per-unit costs competitive without sacrificing transit time.',
  },
  {
    q: 'What is your minimum shipment size?',
    a: 'There is no hard minimum. We work with small businesses shipping pallet quantities as well as enterprise clients shipping full truckloads daily. Rates are scaled to volume — smaller shipments are quoted on a per-case basis.',
  },
  {
    q: 'How do you handle damaged or lost freight?',
    a: 'All shipments are covered under carrier liability. We also offer additional cargo insurance and handle the claims process on your behalf — you don\'t have to deal with carriers directly. We aim to resolve all damage claims within 30 days.',
  },
  {
    q: 'What are your warehousing terms?',
    a: 'Warehousing is available on monthly agreements with no long-term lock-in for most clients. We offer inventory management, pick-and-pack, cross-docking, and climate-controlled storage. Short-term overflow storage during peak season is also available.',
  },
  {
    q: 'How quickly can you onboard a new client?',
    a: 'Most new clients are operational within 48–72 hours of approving a proposal. Emergency onboarding for urgent freight needs can be done same-day in most cases. We keep the paperwork minimal.',
  },
  {
    q: 'Are you licensed and insured?',
    a: 'Yes. Renuit Logistics is fully licensed with the FMCSA, bonded, and carries both carrier liability and cargo insurance. Documentation is available upon request as part of any client onboarding package.',
  },
];

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    className={`rn-faq__chevron${open ? ' is-open' : ''}`}
    width="18" height="18" viewBox="0 0 24 24"
    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const FAQPage = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  const toggle = (i: number) => setOpenIdx(prev => prev === i ? null : i);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
    <section className="rn-faq">
      <div className="rn-faq__inner">

        <div className="rn-faq__header">
          <span className="cs-topper">FAQ</span>
          <h2 className="rn-faq__heading">Common Questions Answered</h2>
          <p className="rn-faq__sub">
            The questions freight managers ask before making a call — answered up front.
          </p>
        </div>

        <ul className="rn-faq__list">
          {FAQS.map(({ q, a }, i) => (
            <li key={q} className={`rn-faq__item${openIdx === i ? ' is-open' : ''}`}>
              <button
                className="rn-faq__question"
                onClick={() => toggle(i)}
                aria-expanded={openIdx === i}
              >
                <span>{q}</span>
                <ChevronIcon open={openIdx === i} />
              </button>
              <div className="rn-faq__answer" aria-hidden={openIdx !== i}>
                <p>{a}</p>
              </div>
            </li>
          ))}
        </ul>

        <p className="rn-faq__footer">
          Don't see your question?{' '}
          <a href="#contact" className="rn-faq__link">Ask us directly →</a>
        </p>

      </div>
    </section>
    </>
  );
};
